// The order the machines will actually work, applied to the Plan-ahead list.
//
// Plan ahead shows every drum that is LAUNCHED and QUEUED but not made yet,
// across every product, phase and machine. It was in whatever order the tokens
// came out of Redis, which is job-creation order and has nothing to do with the
// floor. Asked for 2026-11-02:
//
//   *"Problema mea este ca nu am o ordine aici si tot timpul trebuie sa caut cam
//   care ar fi ordinea de executie. M-ar ajuta foarte mult daca mi-ar aparea
//   conform ordinii executiilor de la masini … as vrea sa am la inceput de tabel
//   primele executii care ies. La fel pentru toate fazele."*
//
// ── A FACT BEATS AN ESTIMATE ──────────────────────────────────────────────
//
// Two different things can order these rows, and they are not equally good:
//
//   the machine's QUEUE POSITION   a fact. The list the operator will work
//                                  through, in the order somebody put it in.
//   the plan's `endAt`             an estimate, from measured speeds — and null
//                                  whenever a block has no measurement.
//
// So within ONE machine the queue decides, always, even if the dates disagree:
// *"poate te ajuta si ordinea produselor din queue-ul de la fiecare masina"*
// (user, 2026-11-02). A speed estimate must never reorder a queue the operator
// is going to follow regardless.
//
// Across machines there is nothing factual to compare — l75 position 2 and l70
// position 2 say nothing about each other.
//
// ── SO MACHINES ARE BLOCKS, NOT INTERLEAVED ───────────────────────────────
//
// The first version interleaved them by date. It read as chaos on the floor:
//
//   *"pare sa imi apara in ordinea executiei dar ma incurca cand vreau sa
//   selectez de exemplu izolarea de la l70 pentru ca imi apare 1 token de la
//   l75 … Poate ar trebui ori un filtru dupa masina, ori o coloana dupa masina
//   care sa filtreze in ordinea in care le iese din queue."*  (2026-11-02)
//
// So each machine is a CONTIGUOUS BLOCK, in its own queue order, and the blocks
// are ordered by whichever delivers soonest. The planner works a machine at a
// time, and that is how the table now reads. Nothing is lost from the original
// ask — the first row is still the first thing coming off — and the one part
// that was an estimate now only decides which BLOCK comes first, never which
// drum.
//
// `endAt` rather than `startAt` for that block order, because this view exists
// to schedule the NEXT phase off a drum that does not exist yet: the question is
// when it comes OFF. With no measured speed anywhere (rope often has none) every
// `endAt` is null and the blocks fall back to machine name — arbitrary but
// stable, and each machine's own sequence is still exact.
//
// ── where the order comes from ────────────────────────────────────────────
//
// `/production-plan` with `queues: true` — the factory-wide schedule, which
// already walks each machine's real queue in order and accounts for machine
// contention (helpers/productionPlan.js `planQueues`, which stamps `queueIndex`
// onto every step from the live `machineQueue:<id>` lists). Nothing new is
// computed here and nothing is guessed: this module only JOINS and SORTS.
//
// The join key is `jobId::taskId`, the plan's own `step.key`. `jobId + phase +
// machine` is not a substitute — ten MYF drums are ten tasks of one phase of one
// job on one machine, which is exactly the case the ordering is for.
//
// Pure, and mirrored into tests/.generated by tests/tools/mirror.mjs.

// ABSENT, not zero.
//
// `Number(null)` is 0 and `Number('')` is 0, and 0 is a perfectly good
// timestamp and a perfectly good queue position — the FIRST one. So the obvious
// `Number.isFinite(Number(v)) ? Number(v) : null` turns "the plan could not date
// this run" into "it finishes at the epoch, before everything" and "this run has
// no queue position" into "it is at the head of the queue". Both sort a row to
// the very top of the table, which is the one place a row nobody can place must
// not be. Guard the empties FIRST. [[feedback_object_field_collision]]
const num = v => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The plan's key for a row — the same one `planFactory` stamps on a step. */
export function stepKeyOf(row) {
  const jobId = row?.jobId == null ? '' : String(row.jobId);
  const taskId = row?.taskId == null ? '' : String(row.taskId);
  return jobId && taskId ? `${jobId}::${taskId}` : '';
}

/**
 * `jobId::taskId` → where that run sits, and when it finishes if anyone knows.
 *
 * ── TWO SOURCES, AND THE POSITIONS ARE THE ONE THAT MATTERS ───────────────
 *
 * `queuePositions` is every queued task with its machine and index — a fact,
 * present for all of them. `steps` adds dates, and exists only for tasks the
 * plan could TIME: `planFactory` drops anything it cannot give a duration and
 * reports it as `NO_SPEED`.
 *
 * Built from the steps alone, this map lost whole machines. Measured on the live
 * factory 2026-11-02: 37 queued tasks, **0 steps** when no speed is known, and
 * in production skip2250 and dtb1250 — which have no measured rope speed —
 * disappeared from a view that happily listed l75, l70, l120 and ath600:
 *
 *   *"nu apar toate masinile … imi apare not queue (5) in loc de skip2250,
 *   dtb1250."*
 *
 * They were queued the whole time. A drum is placeable because it is ON A
 * QUEUE, never because somebody has measured that line's speed.
 *
 * Steps with no `endAt`, and tasks with no step at all, keep `endAt: null`.
 */
export function buildExecutionOrder(steps, queuePositions) {
  const out = new Map();

  // The facts first.
  for (const p of Array.isArray(queuePositions) ? queuePositions : []) {
    const key = String(p?.key || '');
    if (!key) continue;
    out.set(key, {
      startAt: null,
      endAt: null,
      machineId: p.machineId ? String(p.machineId).toLowerCase() : null,
      queued: true,
      queueIndex: num(p.index),
      phase: p.phase ?? null
    });
  }

  // Then the dates, where the plan has them. A step never REMOVES a position —
  // it only fills in what the queue could not say.
  for (const s of Array.isArray(steps) ? steps : []) {
    const key = String(s?.key || '');
    if (!key) continue;
    const had = out.get(key);
    out.set(key, {
      startAt: num(s.startAt),
      endAt: num(s.endAt),
      machineId:
        (s.machineId ? String(s.machineId).toLowerCase() : null) ?? had?.machineId ?? null,
      queued: had ? true : !!s.queued,
      // The queue is the authority on position; a step's own copy is a mirror of
      // it and `null` on anything the plan merely projected.
      queueIndex: had?.queueIndex ?? num(s.queueIndex),
      phase: s.phase ?? had?.phase ?? null
    });
  }

  return out;
}

/**
 * The Plan-ahead rows, in the order the drums will come off.
 *
 * Returns a NEW array of the same row objects with `_exec` stamped on a shallow
 * copy — the table reads `_exec.rank` and `_exec.endAt` to show the order rather
 * than leaving it implicit. Nothing else about a row is touched.
 *
 * A row the plan says nothing about goes LAST and is marked (`_exec.ranked` is
 * false). Sinking it silently would be the worse failure: a drum that is queued
 * nowhere is exactly the one somebody needs to notice.
 */
export function orderPlannedTokens(rows, order) {
  const list = Array.isArray(rows) ? rows : [];
  const map = order instanceof Map ? order : new Map();

  // The index each row arrived at, so the sort is STABLE where the plan cannot
  // separate two rows. Several drums of ONE run share a step — their order
  // within it is the order they were created, which is the order they are wound.
  const decorated = list.map((row, i) => {
    const step = map.get(stepKeyOf(row)) || null;
    return { row, i, step };
  });

  const byDate = (a, b) => {
    for (const f of ['endAt', 'startAt']) {
      const av = a?.[f];
      const bv = b?.[f];
      if (av != null && bv != null && av !== bv) return av - bv;
      // A dated run comes before an undated one: something known beats nothing.
      if (av != null && bv == null) return -1;
      if (av == null && bv != null) return 1;
    }
    return 0;
  };

  // WHICH BLOCK COMES FIRST — the machine that delivers soonest. Computed from
  // each machine's EARLIEST drum, so one long run late in a queue cannot push
  // its whole machine down the table.
  const blockRank = new Map();
  for (const d of decorated) {
    if (!d.step) continue;
    const m = d.step.machineId || '';
    const best = blockRank.get(m);
    if (!best || byDate(d.step, best) < 0) blockRank.set(m, d.step);
  }

  const rank = (a, b) => {
    const aHas = !!a.step;
    const bHas = !!b.step;
    if (aHas !== bHas) return aHas ? -1 : 1; // unscheduled last
    if (!aHas) return a.i - b.i;

    const am = a.step.machineId || '';
    const bm = b.step.machineId || '';

    // DIFFERENT MACHINES → whole blocks, ordered by the machine that delivers
    // soonest. Never drum-against-drum: that is what made the table read as
    // chaos, with one l75 row sitting in the middle of l70's isolation.
    if (am !== bm) {
      const d = byDate(blockRank.get(am), blockRank.get(bm));
      if (d) return d;
      // No dates anywhere — arbitrary but STABLE, so the table does not
      // reshuffle itself between polls.
      return am < bm ? -1 : 1;
    }

    // SAME MACHINE → the queue, which is a fact. Never the dates: a speed
    // estimate must not reorder a list the operator is going to work through in
    // the order it is in.
    const aq = a.step.queueIndex;
    const bq = b.step.queueIndex;
    if (aq != null && bq != null && aq !== bq) return aq - bq;
    if (aq != null && bq == null) return -1;
    if (aq == null && bq != null) return 1;
    // Several drums of ONE run share a step — their order within it is the
    // order they were created, which is the order they are wound.
    return byDate(a.step, b.step) || a.i - b.i;
  };

  decorated.sort(rank);

  let prevMachine = null;
  return decorated.map(({ row, step }, idx) => {
    const machineId = step?.machineId ?? null;
    // The first row of a machine's block, so the table can draw the boundary
    // instead of leaving the planner to spot where one machine ends.
    const blockFirst = machineId !== prevMachine;
    prevMachine = machineId;
    return {
      ...row,
      _exec: {
        // 1-based, and only on rows the plan placed — a rank on an unscheduled
        // drum would read as a position it does not have.
        rank: step ? idx + 1 : null,
        ranked: !!step,
        blockFirst,
        startAt: step?.startAt ?? null,
        endAt: step?.endAt ?? null,
        machineId,
        queueIndex: step?.queueIndex ?? null,
        queued: !!step?.queued
      }
    };
  });
}

/** How many rows the plan could place, for a count the view can state. */
export function countRanked(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => r?._exec?.ranked).length;
}

/**
 * The machines present, in the order their blocks appear — so a filter's
 * options read down the table rather than alphabetically against it.
 *
 * Call it on the ORDERED rows. Rows the plan could not place contribute no
 * machine; they are reachable through the filter's "not queued" option, which
 * the caller adds, because a drum queued nowhere is one somebody has to find.
 */
export function machinesOf(rows) {
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    const m = r?._exec?.machineId;
    if (!m || seen.has(m)) continue;
    seen.add(m);
    out.push(m);
  }
  return out;
}
